<script setup lang="ts">
// The shared bottom ledger: a tab per object type, one dense table at a time,
// resized by the same drawer widget Map Review and Operations already use. The
// page owns the active tab, the height, and the selection; this component draws
// them and emits what the operator did.
//
// The filters are the one state kept here, because they are per tab: the page
// declares what each tab can be narrowed by and this component holds the values,
// one map per tab key. Arriving at a tab opens it whole, so a clause set on
// Signals cannot silently narrow Leads. A server-backed page declares
// `serverFiltered` instead: it supplies rows already narrowed, and the bar, the
// count and the narrowing all stay with the server.
import { computed, ref, watch } from "vue";

import BottomDrawer from "@/components/panel/BottomDrawer.vue";

import FilterBar from "./FilterBar.vue";
import type {
	LedgerFilter,
	LedgerFilterValue,
	LedgerRow,
	LedgerSelection,
	LedgerTab,
} from "./ledger";
import { activeClauses, narrowRows } from "./ledger";
import LedgerTable from "./LedgerTable.vue";

interface Props {
	tabs: LedgerTab[];
	activeKey: string;
	fraction: number;
	expanded: boolean;
	selection: LedgerSelection | null;
	multi?: boolean;
	selectedKeys?: Set<string>;
	/** What the active tab can be narrowed by; empty means no filter bar. */
	filters?: LedgerFilter[];
	/** The rows arrive narrowed by the server, so this drawer neither narrows
	 * nor counts them. */
	serverFiltered?: boolean;
	/** A drawn area in force, named by the page; it narrows the tab exactly as a
	 * filter clause does, by the membership the draw selected. */
	areaLabel?: string | null;
	/** The keys the drawn area selected, or null when no area is in force. */
	areaKeys?: Set<string> | null;
}

const props = withDefaults(defineProps<Props>(), {
	multi: false,
	selectedKeys: () => new Set<string>(),
	filters: () => [],
	serverFiltered: false,
	areaLabel: null,
	areaKeys: null,
});

const emit = defineEmits<{
	(e: "update:activeKey", key: string): void;
	(e: "select", row: LedgerRow, additive: boolean): void;
	(e: "toggle", row: LedgerRow): void;
	(e: "update:fraction", fraction: number): void;
	(e: "update:expanded", expanded: boolean): void;
	(
		e: "filter-change",
		tabKey: string,
		values: Record<string, LedgerFilterValue>,
	): void;
	(e: "clear-area"): void;
}>();

const activeTab = computed(
	() => props.tabs.find((tab) => tab.key === props.activeKey) ?? props.tabs[0],
);

const filtersByTab = ref<Record<string, Record<string, LedgerFilterValue>>>({});

const activeValues = computed<Record<string, LedgerFilterValue>>(
	() => filtersByTab.value[props.activeKey] ?? {},
);

watch(
	() => props.activeKey,
	(key) => {
		filtersByTab.value = { ...filtersByTab.value, [key]: {} };
	},
);

/** A change the operator made, so a server-backed page can turn it into a
 * query. Landing on a tab is not one: nothing was narrowed. */
function onFilterChange(values: Record<string, LedgerFilterValue>) {
	const key = props.activeKey;
	filtersByTab.value = { ...filtersByTab.value, [key]: values };
	emit("filter-change", key, values);
}

const visibleRows = computed<LedgerRow[]>(() => {
	const tab = activeTab.value;
	if (!tab) return [];
	if (props.serverFiltered) return tab.rows;
	return narrowRows(
		tab.rows,
		props.filters,
		activeValues.value,
		props.areaKeys,
	);
});

const clauses = computed(() =>
	props.serverFiltered ? [] : activeClauses(props.filters, activeValues.value),
);

/** "N of M", and only when this drawer did the narrowing: a server-backed tab's
 * total is the server's answer, not the length of the page it handed over. */
const countCaption = computed(() => {
	const tab = activeTab.value;
	if (!tab || props.serverFiltered) return null;
	if (clauses.value.length === 0 && props.areaKeys === null) return null;
	return `${visibleRows.value.length} of ${tab.rows.length}`;
});
</script>

<template>
	<BottomDrawer
		class="workbench-ledger"
		:fraction="fraction"
		:expanded="expanded"
		@update:fraction="emit('update:fraction', $event)"
		@update:expanded="emit('update:expanded', $event)"
	>
		<template #grab>
			<div class="ledger-tabs" role="tablist" aria-label="Ledger tables">
				<button
					v-for="tab in tabs"
					:key="tab.key"
					type="button"
					role="tab"
					class="ledger-tab"
					:class="{ active: tab.key === activeKey }"
					:aria-selected="tab.key === activeKey"
					@pointerdown.stop
					@click="emit('update:activeKey', tab.key)"
				>
					<span>{{ tab.label }}</span>
					<span class="ledger-tab-count">{{ tab.badgeCount }}</span>
				</button>
			</div>
		</template>

		<!-- The bar's trailing end belongs to the page: a tab whose collection
		     can be added to puts its primary action here, beside the switcher
		     and outside the drag target. -->
		<template #grab-end><slot name="grab-end" /></template>

		<FilterBar
			v-if="!serverFiltered && (filters.length > 0 || areaLabel)"
			:filters="filters"
			:values="activeValues"
			:area="areaLabel ? { label: areaLabel } : null"
			@update:values="onFilterChange"
			@clear="onFilterChange({})"
			@clear-area="emit('clear-area')"
		/>

		<p v-if="countCaption" class="ledger-caption" aria-live="polite">
			{{ countCaption }}
		</p>

		<LedgerTable
			v-if="activeTab"
			:columns="activeTab.columns"
			:rows="visibleRows"
			:selection="selection"
			:default-sort="activeTab.defaultSort"
			:multi="multi"
			:selected-keys="selectedKeys"
			@select="(row, additive) => emit('select', row, additive)"
			@toggle="(row) => emit('toggle', row)"
		/>
	</BottomDrawer>
</template>

<style scoped>
/* Placement only: the drawer widget owns its bar and the flex column. */
.workbench-ledger {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 20;
	background: #fff;
	border-top: 1px solid #ced4da;
	box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.12);
	min-height: 140px;
}

.ledger-tabs {
	display: flex;
	gap: 2px;
	min-width: 0;
	overflow: hidden;
}

/* The count under the filter bar: one muted line, so the table below keeps the
   drawer's height and the operator still sees how much of the tab remains. */
.ledger-caption {
	margin: 0;
	padding: 1px 6px;
	flex: 0 0 auto;
	color: #6c757d;
	font-size: 0.7rem;
	line-height: 16px;
	background: #fff;
	border-bottom: 1px solid #eef0f2;
	font-variant-numeric: tabular-nums;
}

.ledger-tab {
	display: flex;
	align-items: baseline;
	gap: 6px;
	height: 28px;
	padding: 0 10px;
	border: none;
	background: transparent;
	color: #495057;
	font-size: 0.75rem;
	font-weight: 600;
	cursor: pointer;
	border-bottom: 2px solid transparent;
}

.ledger-tab:hover {
	background: #f1f3f5;
}

.ledger-tab.active {
	color: #0d6efd;
	border-bottom-color: #0d6efd;
}

.ledger-tab-count {
	color: #6c757d;
	font-weight: 500;
	font-variant-numeric: tabular-nums;
}
</style>
